/**
 * A lógica de apresentação das sete telas — sem JSX, para poder ser testada.
 *
 * POR QUE SEPARADA DO COMPONENTE: as decisões que erram caro aqui não são de
 * layout, são de leitura. "Este bloco abre ou fecha?", "esta nota pode ser
 * exibida?", "qual resultado é o de caixa e qual é o de competência?" — cada
 * uma dessas já foi respondida errado neste produto pelo menos uma vez, e
 * nenhuma delas precisa de um navegador para ser verificada.
 *
 * REGRA HERDADA DA CAMADA DE DIAGNÓSTICO: nada aqui converte ausência em zero.
 * O formatador devolve `—` e quem lê decide o que dizer; um `Number(x) || 0`
 * neste arquivo faria a tela afirmar uma medição que ninguém fez.
 */

import {
  SECOES,
  CAMPOS_DO_BALANCO_CURTO,
  CATALOGO_DE_INDICADORES,
  porChave,
  secaoExigeBalanco,
  indicadoresBloqueadosSemBalanco,
  telaDoIndicador
} from '../diagnostico/catalogo';
import { CATALOGO_DE_FAIXAS, classificar, semFaixaDeReferencia } from '../diagnostico/faixas';
import { motivoDaAusencia } from '../diagnostico/leitura';
import { faixaDoIndice, seloDoPerfil } from '../diagnostico/frases';
import { BALANCO_CAMPOS } from '../config/planoContas';
import { rotuloDaCompetencia, rotuloLongoDaCompetencia } from '../services/loteDoMes';
import {
  TRACO,
  formatarMoeda,
  formatarPercentual,
  formatarDecimal,
  ultimoIndiceComDado,
  numeroOuNulo
} from '../utils/series';

/**
 * Dinheiro sem centavos no nível de bloco.
 *
 * Centavos num número de R$ 118.500 são precisão falsa: ninguém decide nada com
 * eles e as colunas ficam mais difíceis de ler. Os demonstrativos, que existem
 * para conferir conta a conta com o contador, continuam com centavos.
 */
const MOEDA_CURTA = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0
});

export const formatarMoedaCurta = (valor) =>
  valor === null || valor === undefined || Number.isNaN(valor) ? TRACO : MOEDA_CURTA.format(valor);

/** Dias inteiros: meio dia de prazo médio não muda decisão nenhuma. */
export const formatarDias = (valor) =>
  valor === null || valor === undefined || Number.isNaN(valor)
    ? TRACO
    : `${Math.round(valor)} d`;

/** O formato declarado no catálogo vira texto. Ausência sempre vira `—`. */
export const formatarPorTipo = (valor, formato) => {
  switch (formato) {
    case 'moeda':
      return formatarMoedaCurta(valor);
    case 'moedaExata':
      return formatarMoeda(valor);
    case 'percentual':
      return formatarPercentual(valor, 1);
    case 'dias':
      return formatarDias(valor);
    case 'ratio':
    case 'decimal':
      return formatarDecimal(valor, 2);
    default:
      return formatarDecimal(valor, 2);
  }
};

/** Os 10 campos do balanço completo, gerados do formulário e não da mão. */
export const CAMPOS_DO_BALANCO_COMPLETO = [
  ...BALANCO_CAMPOS.ativo,
  ...BALANCO_CAMPOS.passivo,
  ...BALANCO_CAMPOS.patrimonio
].map((c) => c.nome);

/**
 * Quanto de balanço cada seção exige.
 *
 * Não é tudo ou nada: liquidez e ciclo abrem com os 5 campos do balanço curto,
 * enquanto endividamento e retorno precisam do ativo não circulante e do
 * patrimônio líquido — ou seja, do balanço inteiro. Dizer "informe o balanço"
 * para as duas coisas cobra do usuário um trabalho maior do que o necessário.
 */
export const DEGRAU_DO_BALANCO = {
  [SECOES.LIQUIDEZ]: 'curto',
  [SECOES.CICLO]: 'curto',
  [SECOES.ESTRUTURA_CAPITAL]: 'completo'
};

/**
 * Se a seção abre inteira, fecha inteira, e o que o CTA precisa dizer.
 *
 * A degradação é por BLOCO. Meio bloco preenchido espalha buraco pela tela e
 * faz o usuário concluir que o produto está quebrado, em vez de concluir que
 * falta dado dele.
 *
 * @returns {{aberta: boolean, degrau: string|null, campos: string[], bloqueados: string[], chamada: string|null}}
 */
export const estadoDaSecao = (leitura, secao) => {
  const exige = secaoExigeBalanco(secao);
  const temBalanco = Boolean(leitura?.temBalanco);

  if (!exige || temBalanco) {
    return { aberta: true, degrau: null, campos: [], bloqueados: [], chamada: null };
  }

  const degrau = DEGRAU_DO_BALANCO[secao] || 'completo';
  const campos = degrau === 'curto' ? CAMPOS_DO_BALANCO_CURTO : CAMPOS_DO_BALANCO_COMPLETO;
  const bloqueados = indicadoresBloqueadosSemBalanco(secao);

  return {
    aberta: false,
    degrau,
    campos,
    bloqueados,
    chamada: `${campos.length} campos do balanço destravam ${bloqueados.length} ${
      bloqueados.length === 1 ? 'indicador' : 'indicadores'
    } deste bloco`
  };
};

/**
 * O Índice de Saúde pronto para a tela — e a única regra que importa aqui é
 * QUANDO ELE NÃO PODE SER EXIBIDO.
 *
 * O motor devolve `nota: 0` quando nenhum pilar entrou, porque `0` é o neutro
 * da soma. Exibir esse zero pintaria de vermelho uma empresa que apenas não
 * informou nada — é a mesma família do defeito em que `null` era carimbado como
 * "Crítico" na tabela resumo. Sem pilar considerado, a nota não existe.
 *
 * Fora isso a nota SEMPRE aparece, inclusive para quem não tem balanço: o selo
 * qualifica, e qualificar é melhor que esconder.
 */
export const resumoDoIndice = (indiceSaude) => {
  const cobertura = indiceSaude?.cobertura || {};
  const considerados = cobertura.pilaresConsiderados ?? 0;
  const pilares = indiceSaude?.pilares || [];

  if (!indiceSaude || considerados === 0) {
    return {
      calculado: false,
      nota: null,
      rotulo: 'Não calculado',
      cor: 'default',
      selo: 'Nenhum pilar do índice tem dado suficiente ainda.',
      dentro: [],
      fora: pilares.filter((p) => !p.entrou)
    };
  }

  const faixa = faixaDoIndice(indiceSaude.nota);
  return {
    calculado: true,
    nota: indiceSaude.nota,
    rotulo: faixa.rotulo,
    cor: faixa.cor,
    perfil: indiceSaude.perfil,
    selo: seloDoPerfil(indiceSaude),
    dentro: pilares.filter((p) => p.entrou),
    fora: pilares.filter((p) => !p.entrou)
  };
};

/**
 * Um indicador pronto para exibir: nome, texto, faixa e — quando falta — o
 * MOTIVO de faltar.
 *
 * O motivo vem do motor (`indicadores.meta`), que já nomeia o campo ausente por
 * indicador. Escrever um texto próprio aqui criaria uma segunda explicação para
 * a mesma falta, e as duas divergiriam no primeiro campo novo do formulário.
 */
/**
 * A régua com que este indicador foi julgado, em português.
 *
 * O produto exibia o número, a faixa e o veredito — e escondia o critério. Quem
 * recebe "Atenção" num indicador que não conhece fica sabendo que algo está
 * mal, sem saber o que seria estar bem, nem quanto falta. A régua responde as
 * duas coisas com os mesmos cortes que a classificação usou: não é texto novo
 * sobre o indicador, é o critério que já existia, dito em voz alta.
 */
export const reguaDoIndicador = (chaveDaFaixa, formato) => {
  const faixa = CATALOGO_DE_FAIXAS[chaveDaFaixa];
  if (!faixa || !Array.isArray(faixa.cortes) || faixa.cortes.length !== 3) return null;

  const [a, b, c] = faixa.cortes.map((corte) => formatarPorTipo(corte, formato));

  const degraus =
    faixa.direcao === 'maiorMelhor'
      ? [
          { rotulo: 'Crítico', valor: `abaixo de ${a}` },
          { rotulo: 'Atenção', valor: `${a} a ${b}` },
          { rotulo: 'Bom', valor: `${b} a ${c}` },
          { rotulo: 'Excelente', valor: `acima de ${c}` }
        ]
      : [
          { rotulo: 'Excelente', valor: `até ${a}` },
          { rotulo: 'Bom', valor: `${a} a ${b}` },
          { rotulo: 'Atenção', valor: `${b} a ${c}` },
          { rotulo: 'Crítico', valor: `acima de ${c}` }
        ];

  return { degraus, fonte: faixa.fonte || null };
};

export const montarIndicador = (leitura, chave) => {
  const item = porChave[chave];
  if (!item) return null;

  const valor = leitura?.valores?.[chave] ?? null;
  const faixa = classificar(item.faixa, valor);

  return {
    chave,
    nome: item.nome,
    formato: item.formato,
    valor,
    texto: formatarPorTipo(valor, item.formato),
    nivel: faixa.nivel,
    rotulo: faixa.rotulo,
    cor: faixa.cor,
    fonte: faixa.fonte,
    exigeBalanco: item.exigeBalanco,
    regua: reguaDoIndicador(item.faixa, item.formato),
    motivo: valor === null ? motivoDaAusencia(leitura, chave, item.exigeBalanco) : null
  };
};

/**
 * Todos os indicadores de uma seção, na ordem do catálogo.
 *
 * `exceto` existe por um motivo específico: o número protagonista do bloco já
 * está exibido em corpo grande, com faixa e frase. Repeti-lo na lista de apoio
 * imprime o MESMO valor duas vezes na mesma dobra — que é, literalmente, a
 * doença que este redesign existe para curar (quatro pares de indicadores
 * exibindo o mesmo número com nomes diferentes).
 */
export const montarSecao = (leitura, secao, exceto = []) =>
  CATALOGO_DE_INDICADORES
    .filter((i) => i.secao === secao && !exceto.includes(i.chave))
    .map((i) => montarIndicador(leitura, i.chave))
    .filter(Boolean);

/**
 * O índice do mês mais recente COM medição — e não a última posição do vetor.
 *
 * A janela pode terminar num mês ainda não lançado. Todo número da escada de
 * resultado é lido neste mesmo índice de propósito: misturar o lucro de março
 * com o CMV de fevereiro produziria uma subtração que não fecha, e é a
 * reconciliação com a planilha do cliente que dá ao consultor a capacidade de
 * provar o número.
 */
export const indiceDoMesRecente = (leitura) =>
  ultimoIndiceComDado(leitura?.series?.faturamento || []);

const em = (serie, i) => {
  if (i < 0 || !Array.isArray(serie)) return null;
  const valor = serie[i];
  return valor === undefined ? null : valor;
};

/**
 * Os DOIS resultados, com o CMV explícito entre eles.
 *
 * Decisão do dono, e ela existe porque a planilha do cliente se contradiz: o
 * "Lucro Real" dela exclui a compra de mercadoria (correto, é desembolso de
 * estoque) e nunca soma o CMV de volta, enquanto o ponto de equilíbrio dela usa
 * o CMV. Faturando abaixo do ponto de equilíbrio a planilha ainda mostra lucro.
 *
 * Corrigir em silêncio faria o dashboard divergir do arquivo do cliente;
 * repetir o número dela sem ressalva publicaria um lucro que o consultor sabe
 * estar alto. Exibir os dois, com a diferença nomeada, é a única saída que não
 * esconde nada — e é exatamente o tipo de leitura que o consultor cobra para
 * fazer.
 */
/**
 * A MERCADORIA JÁ ESTÁ DENTRO DO CUSTO DESTE MÊS?
 *
 * Um lugar só, porque a resposta governa duas telas e elas não podem divergir.
 *
 * A planilha do cliente separa caixa de competência: o `Custo Total Real` dela
 * exclui a compra de mercadoria de propósito, então o CMV é uma parcela à parte
 * e a subtração acontece. O questionário guiado pergunta a mercadoria como
 * custo do mês, então o custo total JÁ a inclui e subtrair de novo contaria o
 * mesmo dinheiro duas vezes.
 *
 * Em vez de perguntar de onde veio o lançamento, a checagem confere qual
 * subtração REPRODUZ o resultado em competência que o motor calculou. O que não
 * fecha não é exibido como se fechasse.
 */
export const cmvJaDentroDoCusto = (faturamento, cmv, custoTotalReal, resultado) => {
  if (!(faturamento > 0) || cmv === null || custoTotalReal === null || resultado === null) {
    return { fechaSeparado: false, fechaDentro: false, reconcilia: false };
  }
  // Meio por cento do faturamento, com piso de um real: a planilha arredonda, e
  // um centavo de diferença não é uma definição diferente de custo.
  const tolerancia = Math.max(1, faturamento * 0.005);
  const fechaSeparado = Math.abs(faturamento - cmv - custoTotalReal - resultado) <= tolerancia;
  const fechaDentro = Math.abs(faturamento - custoTotalReal - resultado) <= tolerancia;
  return { fechaSeparado, fechaDentro, reconcilia: fechaSeparado || fechaDentro };
};

export const resumoDosResultados = (leitura) => {
  const i = indiceDoMesRecente(leitura);
  const s = leitura?.series || {};

  const faturamento = em(s.faturamento, i);
  const receitaLiquida = em(s.receitaLiquida, i);
  const cmv = em(s.cmv, i);
  const custoTotalReal = em(s.custoTotalReal, i);
  const caixa = em(s.lucroCaixa, i);
  const competencia = em(s.lucroCompetencia, i);

  const deducoes =
    faturamento === null || receitaLiquida === null ? null : faturamento - receitaLiquida;

  // A ESCADA MOSTRAVA UMA SUBTRAÇÃO QUE NÃO ACONTECEU.
  //
  // O motor deixou de descontar o CMV para lançamento de questionário — nele a
  // mercadoria já é custo do mês —, mas a tela continuou emitindo o degrau
  // "(−) Custo da mercadoria vendida R$ 48.000" entre dois resultados de
  // R$ 12.100. O produto exibia o mesmo número com dois rótulos, a mesma margem
  // de 10,5% duas vezes, e no meio deles uma subtração de quarenta e oito mil
  // reais que ninguém fez. Um consultor que confere isso na frente do cliente
  // perde o cliente.
  const conciliacao = cmvJaDentroDoCusto(faturamento, cmv, custoTotalReal, competencia);
  const cmvDentroDoCusto = conciliacao.reconcilia
    ? !conciliacao.fechaSeparado
    // Sem reconciliar não se afirma nem uma coisa nem outra: cai no
    // comportamento herdado da planilha, que é a origem histórica do dado.
    : false;

  // A ESCADA SÓ EXISTE QUANDO HÁ DOIS RESULTADOS DE VERDADE.
  //
  // `cmvDentroDoCusto` cobre o lançamento que reconcilia, mas ele não é o único
  // jeito de os dois números coincidirem. Medido na tela, com a Padaria Aurora:
  // sem CMV informado a escada saía "RESULTADO DE CAIXA R$ 24.145", "(−) Custo
  // da mercadoria vendida —", "RESULTADO COM CMV R$ 24.145". Uma subtração de
  // NADA entre dois números iguais, com nomes diferentes — o mesmo defeito, por
  // outra porta.
  //
  // A regra que vale para todas as portas: só há escada se existir a parcela a
  // subtrair E se subtraí-la mudar o resultado.
  const doisResultados =
    cmv !== null
    && caixa !== null
    && competencia !== null
    && Math.abs(caixa - competencia) >= 1;

  // QUANDO SÓ HÁ UM RESULTADO, ELE NEM SEMPRE É O DE COMPETÊNCIA.
  //
  // Planilha sem a linha de CMV: o motor não pode afirmar o resultado em
  // competência (subtrair um CMV desconhecido daria um número inventado), e
  // `lucro.competencia` sai `null` — corretamente. Só que a tela exibia esse
  // `null` como travessão no lugar do número protagonista, e o cliente pagante
  // abria a tela Resultado e não via resultado nenhum, embora o resultado de
  // caixa estivesse medido, ali do lado, valendo R$ 3.499.
  //
  // Apagar da tela o único número que a empresa tem é tão errado quanto
  // inventar um. Então o card único passa a exibir o resultado que existe; e
  // como o de caixa não carrega faixa de saúde por construção, exibi-lo não
  // arrisca elogiar margem que ignora o maior custo da empresa.
  const unico = competencia !== null || caixa === null ? 'competencia' : 'caixa';

  return {
    indice: i,
    mes: leitura?.rotulos?.[i] || null,
    unico,
    faturamento,
    deducoes,
    receitaLiquida,
    custoTotalReal,
    compraMercadoria: em(s.compraMercadoria, i),
    cmv,
    // Quando a mercadoria já está no custo do mês, NÃO existem dois resultados:
    // existe um. A tela precisa saber disso para não desenhar a escada.
    cmvDentroDoCusto,
    // E este é o portão da escada: `cmvDentroDoCusto` explica POR QUE só há um
    // resultado; `doisResultados` decide SE há dois.
    doisResultados,
    pontoEquilibrio: em(s.pontoEquilibrio, i),
    caixa: {
      valor: caixa,
      margem: em(s.margemLiquida, i),
      // O resultado de caixa NÃO leva faixa de saúde. Ele omite o CMV por
      // construção, então classificá-lo diria "excelente" a uma empresa cuja
      // margem ignora o maior custo que ela tem — e a faixa carrega justamente
      // o peso de um veredito. Aqui ele é número de RECONCILIAÇÃO: serve para
      // bater com a planilha do cliente, não para julgar a saúde. Quem julga é
      // o resultado em competência, logo ao lado.
      faixa: semFaixaDeReferencia('O resultado de caixa não desconta o CMV — quem mede saúde é o resultado em competência, ao lado.')
    },
    competencia: {
      valor: competencia,
      margem: em(s.margemLiquidaCompetencia, i),
      faixa: classificar('margemLiquida', em(s.margemLiquidaCompetencia, i))
    }
  };
};

/**
 * Para onde vai cada real que entra — a decomposição que RECONCILIA.
 *
 * Base é o faturamento e as três parcelas são CMV, custo operacional do mês
 * (`Custo Total Real`) e o que sobra. Elas fecham em 100% por construção,
 * porque `lucro.competencia` é exatamente essa subtração.
 *
 * Por que não usar "custos variáveis": no arquivo do cliente eles incluem a
 * COMPRA de mercadoria, que é desembolso de estoque e não custo do que foi
 * vendido — no fixture eles sozinhos dão 137% do faturamento. Uma barra
 * "para onde vai cada real" que estoura 100% no primeiro cliente não é
 * decomposição, é confusão.
 *
 * Falta de qualquer uma das três apaga a barra inteira: preencher a parcela que
 * falta com zero seria afirmar que aquele custo não existe.
 */
export const composicaoDoFaturamento = (leitura) => {
  const i = indiceDoMesRecente(leitura);
  const s = leitura?.series || {};

  const faturamento = em(s.faturamento, i);
  const cmv = em(s.cmv, i);
  const custoTotalReal = em(s.custoTotalReal, i);
  if (!(faturamento > 0) || cmv === null || custoTotalReal === null) return null;

  const percentual = (valor) => (valor / faturamento) * 100;
  const resultado = em(s.lucroCompetencia, i);

  // Mesma reconciliação da escada da tela Resultado, e do mesmo lugar: quando
  // as duas divergiam, a barra dizia que a mercadoria era parcela separada
  // enquanto a escada ao lado somava o resultado sem descontá-la.
  const { fechaSeparado: fechaComCmvSeparado, fechaDentro: fechaComCmvDentro } =
    cmvJaDentroDoCusto(faturamento, cmv, custoTotalReal, resultado);

  if (!fechaComCmvSeparado && !fechaComCmvDentro) return null;

  const sobra = resultado;
  const segmentos = fechaComCmvSeparado
    ? [
        { id: 'cmv', rotulo: 'Custo da mercadoria vendida', valor: cmv, percentual: percentual(cmv) },
        {
          id: 'operacional',
          rotulo: 'Custo operacional do mês',
          valor: custoTotalReal,
          percentual: percentual(custoTotalReal)
        }
      ]
    : [
        {
          id: 'operacional',
          rotulo: 'Custo total do mês, mercadoria incluída',
          valor: custoTotalReal,
          percentual: percentual(custoTotalReal)
        }
      ];

  return {
    base: faturamento,
    mes: leitura?.rotulos?.[i] || null,
    cmvDentroDoCusto: !fechaComCmvSeparado,
    segmentos: [
      ...segmentos,
      {
        id: 'resultado',
        rotulo: sobra >= 0 ? 'Sobra para os sócios' : 'Falta para fechar o mês',
        valor: sobra,
        percentual: percentual(Math.abs(sobra)),
        negativo: sobra < 0
      }
    ]
  };
};

/**
 * O que já existe e o que falta — a linha de status da tela de Dados.
 *
 * Ela é persistente de propósito: "DRE em 3 meses · balanço pendente — 15
 * indicadores bloqueados" responde de uma vez o que o usuário tem, o que falta
 * e o que ele ganha completando. O painel de lacunas do wizard dizia isso só
 * durante o preenchimento, e sumia depois.
 */
export const estadoDosDados = (leitura, arquivos = {}) => {
  const cobertura = leitura?.cobertura || null;
  const meses = cobertura?.total ?? (leitura?.periodos || 0);
  const temDRE = meses > 0 || Boolean(arquivos.dre);
  const temBalanco = Boolean(leitura?.temBalanco) || Boolean(arquivos.balanco);
  const bloqueados = temBalanco ? [] : indicadoresBloqueadosSemBalanco();
  const lacunas = cobertura?.lacunas || [];

  const partes = [];
  if (!temDRE) partes.push('DRE pendente');
  else if (meses > 0) partes.push(`DRE em ${meses} ${meses === 1 ? 'mês' : 'meses'}`);
  else partes.push('DRE lançado');
  partes.push(temBalanco ? 'balanço informado' : 'balanço pendente');

  const frase =
    partes.join(' · ') +
    (bloqueados.length
      ? ` — ${bloqueados.length} ${bloqueados.length === 1 ? 'indicador bloqueado' : 'indicadores bloqueados'}`
      : '');

  return { temDRE, temBalanco, meses, cobertura, lacunas, bloqueados, frase };
};

/** O nome de cada seção na tela. Um lugar só, para não divergir entre telas. */
export const TITULO_DA_SECAO = {
  [SECOES.RESULTADO]: 'Resultado',
  [SECOES.ESTRUTURA_CUSTOS]: 'Estrutura de custos',
  [SECOES.LIQUIDEZ]: 'Caixa e liquidez',
  [SECOES.CICLO]: 'Ciclo e capital de giro',
  [SECOES.CUSTO_DIVIDA]: 'Custo da dívida',
  [SECOES.ESTRUTURA_CAPITAL]: 'Estrutura de capital e retorno'
};

/**
 * O painel inteiro, seção por seção, na ordem de leitura.
 *
 * Seção fechada não vira lista de traços: ela aparece fechada, com o CTA, do
 * mesmo jeito que na tela de leitura. Meia dúzia de linhas "—" no meio de uma
 * tabela densa é o buraco espalhado que a degradação por bloco existe para
 * impedir — e num painel de consultor é ainda pior, porque ali o dado é o
 * produto.
 */
export const linhasDoPainel = (leitura) =>
  Object.values(SECOES).map((secao) => {
    const estado = estadoDaSecao(leitura, secao);
    return {
      secao,
      titulo: TITULO_DA_SECAO[secao] || secao,
      aberta: estado.aberta,
      estado,
      itens: estado.aberta ? montarSecao(leitura, secao) : []
    };
  });

/**
 * Todos os sinais determinísticos, cada um apontando para a tela que explica o
 * número dele.
 *
 * Duas fontes convivem no produto: as 39 regras (`insights`, com evidência
 * legível por máquina) e os alertas do motor (`alertas`, com tipo). Elas se
 * sobrepõem em algumas leituras, então a lista é deduplicada pelo começo da
 * mensagem — ver o mesmo problema duas vezes faz o usuário duvidar dos dois.
 */
const ORDEM_DA_SEVERIDADE = { critical: 0, warning: 1, info: 2, positive: 3 };

export const sinaisComTela = (leitura) => {
  const dasRegras = (leitura?.insights?.insights || []).map((i) => ({
    id: i.id || i.message,
    severidade: i.severity || 'info',
    mensagem: i.message,
    recomendacao: i.recommendation || null,
    // O indicador VIAJA JUNTO. Ele era descartado aqui e no mapa do motor, e
    // por isso `assuntoDoSinal` nunca via chave nenhuma: a deduplicação caía
    // 100% das vezes na heurística de substring, que é o plano B.
    indicador: i.evidence?.[0]?.indicator || null,
    tela: telaDoIndicador(i.evidence?.[0]?.indicator),
    origem: 'regra'
  }));

  const alertas = leitura?.alertas || {};
  const doMotor = [
    ...(alertas.criticos || []).map((a) => ({ ...a, severidade: 'critical' })),
    ...(alertas.atencao || []).map((a) => ({ ...a, severidade: 'warning' })),
    ...(alertas.positivos || []).map((a) => ({ ...a, severidade: 'positive' }))
  ].map((a) => ({
    id: `${a.tipo}-${a.mensagem || a.message}`,
    severidade: a.severidade,
    mensagem: a.mensagem || a.message,
    recomendacao: a.recomendacao || null,
    indicador: a.tipo || null,
    tela: telaDoIndicador(a.tipo),
    origem: 'motor'
  }));

  // Deduplicar pelos 40 primeiros caracteres deixava passar o mesmo assunto
  // escrito de dois jeitos: "Liquidez corrente entre 1.0 e 1.5 — situação
  // apertada" e "Liquidez Corrente em atenção (1,47)" viravam dois sinais, e a
  // contagem inflava. A chave certa é o INDICADOR de que o sinal fala.
  const assuntoDoSinal = (s) => {
    const doIndicador = s.indicador || null;
    if (doIndicador) return String(doIndicador).toLowerCase();
    const texto = String(s.mensagem || '').toLowerCase();
    const conhecido = [
      'liquidez corrente', 'liquidez seca', 'liquidez imediata', 'margem líquida',
      'margem de contribuição', 'endividamento', 'faturamento', 'ciclo', 'roe', 'roa',
      'cobertura', 'dscr', 'estoque', 'fornecedor', 'marketing', 'pessoal', 'ponto de equilíbrio'
    ].find((termo) => texto.includes(termo));
    return conhecido || texto.slice(0, 40);
  };

  const vistos = new Set();
  return [...dasRegras, ...doMotor]
    .filter((s) => {
      if (!s.mensagem) return false;
      const chave = `${assuntoDoSinal(s)}|${s.severidade}`;
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    })
    .sort(
      (a, b) =>
        (ORDEM_DA_SEVERIDADE[a.severidade] ?? 9) - (ORDEM_DA_SEVERIDADE[b.severidade] ?? 9)
    );
};

/**
 * A contagem de sinais, em um lugar só.
 *
 * O produto exibia três números diferentes para a mesma empresa: o badge do
 * menu dizia 6, a tela de Análise dizia 8 e a de Planos dizia "outros 4". Cada
 * um contava de um jeito, sobre listas diferentes. Aqui a lista é uma, já sem
 * duplicata, e cada tela pega dela o recorte que precisa.
 */
export const contagemDeSinais = (leitura) => {
  const sinais = sinaisComTela(leitura);
  const criticos = sinais.filter((s) => s.severidade === 'critical').length;
  const atencao = sinais.filter((s) => s.severidade === 'warning').length;
  return {
    total: sinais.length,
    criticos,
    atencao,
    // O badge do menu conta o que pede ação. Sinal positivo é bom saber, não é
    // pendência — contá-lo faria o número crescer quando a empresa melhora.
    pedemAcao: criticos + atencao
  };
};

/**
 * A data em que o parecer foi gerado, legível.
 *
 * O SQLite grava `CURRENT_TIMESTAMP` como "2026-08-19 18:14:44", em UTC e sem
 * fuso declarado. Entregue assim ao `Date`, o navegador lê como hora LOCAL — e
 * um parecer gerado às 21h de ontem apareceria como de hoje. O `Z` explícito é
 * o que impede a data de mentir por um dia inteiro.
 */
const comoData = (valor) => {
  if (!valor) return null;
  const texto = String(valor);
  const iso = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(texto)
    ? `${texto.replace(' ', 'T')}Z`
    : texto;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? null : data;
};

export const dataLegivel = (valor) => {
  const data = comoData(valor);
  return data ? data.toLocaleDateString('pt-BR') : null;
};

/** "o balanço de mai/26" — porque um balanço corrigido não é um mês corrigido. */
const nomeDoLote = ({ tipo, competencia }) =>
  tipo === 'balanco'
    ? `o balanço de ${rotuloLongoDaCompetencia(competencia).toLowerCase()}`
    : rotuloLongoDaCompetencia(competencia);

/** "A", "A e B", "A, B e C" — nunca "3 itens", que manda o usuário procurar quais. */
const listar = (nomes) => {
  if (!nomes.length) return '';
  if (nomes.length === 1) return nomes[0];
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
};

/**
 * De que meses o parecer fala.
 *
 * Acima de quatro competências a lista vira contagem MAIS os extremos, e não um
 * intervalo: a série deste produto aceita lacuna no meio (é caso testado), e
 * escrever "de jan/26 a dez/26" sobre uma série furada afirmaria doze meses que
 * não existem.
 */
export const periodoDoParecer = (competencias) => {
  const lista = (competencias || []).filter(Boolean).slice().sort();
  if (!lista.length) return null;
  if (lista.length === 1) return rotuloLongoDaCompetencia(lista[0]);
  if (lista.length <= 4) return listar(lista.map(rotuloDaCompetencia));
  return `os ${lista.length} meses lançados, de ${rotuloDaCompetencia(lista[0])} `
    + `a ${rotuloDaCompetencia(lista[lista.length - 1])}`;
};

/**
 * A procedência do parecer — exibida SEMPRE que existe parecer, não só quando
 * ele envelhece.
 *
 * "Um parecer de consultoria sem data envelhece invisível": era exatamente o
 * defeito medido na onda anterior. A tela mostrava o texto da IA sem nenhum
 * "gerado em", então o leitor não tinha como saber se aquilo descrevia os
 * números de hoje ou os de três semanas atrás.
 */
export const procedenciaDoParecer = (parecer) => {
  const data = dataLegivel(parecer?.geradoEm);
  const periodo = periodoDoParecer(parecer?.competencias);
  if (!data && !periodo) return null;
  if (!periodo) return `Gerado em ${data}.`;
  if (!data) return `Sobre ${periodo}.`;
  return `Gerado em ${data}, sobre ${periodo}.`;
};

/**
 * O aviso de que a análise foi feita com números que já mudaram.
 *
 * Ele NOMEIA o mês, e separa duas coisas que a tela não pode chamar pelo mesmo
 * nome: mês ALTERADO faz o parecer descrever número que não existe mais; mês
 * NOVO faz o parecer estar incompleto. Aviso que junta os dois vira ruído, e
 * ruído o usuário aprende a ignorar.
 */
export const avisoDeParecerVelho = (parecer) => {
  const velho = parecer?.desatualizado;
  if (!velho) return null;

  const alterados = (velho.alterados || []).map(nomeDoLote);
  const novos = (velho.novos || []).map(nomeDoLote);
  if (!alterados.length && !novos.length) return null;

  const geradoEm = dataLegivel(parecer?.geradoEm);
  const mudouEm = dataLegivel(velho.mudouEm);

  const titulo = alterados.length
    ? `${listar(alterados)} ${alterados.length === 1 ? 'mudou' : 'mudaram'} depois deste parecer`
    : `${listar(novos)} ${novos.length === 1 ? 'entrou' : 'entraram'} depois deste parecer`;

  const partes = [];
  if (alterados.length) {
    partes.push(
      `O parecer abaixo foi escrito${geradoEm ? ` em ${geradoEm}` : ''} com os números que `
      + `${listar(alterados)} ${alterados.length === 1 ? 'tinha' : 'tinham'} antes`
      + `${mudouEm ? `, e eles mudaram em ${mudouEm}` : ''}.`
    );
  }
  if (novos.length && alterados.length) {
    partes.push(`${listar(novos)} ${novos.length === 1 ? 'entrou' : 'entraram'} depois e o parecer não ${novos.length === 1 ? 'o' : 'os'} viu.`);
  } else if (novos.length) {
    partes.push(
      `O parecer abaixo foi escrito${geradoEm ? ` em ${geradoEm}` : ''} sem ${listar(novos)}`
      + `${mudouEm ? `, que ${novos.length === 1 ? 'entrou' : 'entraram'} em ${mudouEm}` : ''}.`
    );
  }
  partes.push(
    'Atualizar refaz a leitura sobre os números de agora. Sem atualizar, ele continua aqui '
    + 'como está — com a data e os meses a que se refere.'
  );

  return { titulo, corpo: partes.join(' ') };
};

/**
 * O estado do parecer por IA, e o que a tela pode dizer sobre ele.
 *
 * Passou a ler `insights.parecer` em vez de `tier === 'llm'`: desde que o
 * parecer virou objeto GRAVADO, ele chega numa resposta cujo `tier` é
 * `deterministico` — a rota de dashboard não aciona mais modelo nenhum. Quem
 * responde "existe parecer?" é o próprio parecer.
 */
export const estadoDoParecer = (leitura) => {
  const llm = leitura?.llm || null;
  const parecer = leitura?.insights?.parecer || null;

  if (llm && llm.permitido === false) {
    return {
      estado: 'bloqueado',
      motivo: llm.motivo || null,
      mensagem: llm.mensagem || 'O parecer por IA não está disponível para esta conta.',
      acao: llm.acao || null
    };
  }

  if (parecer?.gerado) {
    return {
      estado: 'gerado',
      motivo: null,
      mensagem: null,
      acao: null,
      // A procedência viaja junto do estado para que nenhuma tela precise
      // decidir por conta própria se pode exibir a data.
      geradoEm: parecer.geradoEm || null,
      procedencia: procedenciaDoParecer(parecer),
      desatualizado: Boolean(parecer.desatualizado),
      aviso: avisoDeParecerVelho(parecer)
    };
  }

  // Falha técnica NÃO é "não foi gerado": quem paga pelo parecer precisa saber
  // que houve erro do provedor, senão fica esperando um texto que não vem e
  // conclui que comprou vento. O motivo técnico não vai para a tela — vai a
  // informação de que a falha existe e de que os números continuam válidos.
  if (llm && llm.motivo === 'falha') {
    return {
      estado: 'falhou',
      motivo: 'falha',
      mensagem:
        'O parecer por IA não pôde ser gerado agora: o provedor de IA recusou a requisição. '
        + 'Tente de novo em alguns minutos. A análise abaixo é determinística, está completa e não depende dele.',
      acao: null
    };
  }

  return {
    estado: 'naoGerado',
    motivo: null,
    mensagem:
      'O parecer por IA ainda não foi gerado para estes números. A análise abaixo é determinística e está completa.',
    acao: null
  };
};

/** Média dos valores medidos. `null` quando nada foi medido. */
const mediaDosConhecidos = (serie) => {
  const conhecidos = (serie || []).filter((v) => v !== null && v !== undefined);
  return conhecidos.length ? conhecidos.reduce((s, v) => s + v, 0) / conhecidos.length : null;
};

/**
 * A decomposição DuPont — e ela só é exibida se FECHAR.
 *
 * ROE = margem líquida × giro do ativo × multiplicador de alavancagem. Os três
 * fatores precisam sair da mesma base para o produto dar o ROE: o motor calcula
 * o ROE como lucro MÉDIO MENSAL sobre patrimônio líquido, então giro e margem
 * aqui são mensais também. Com base acumulada num lado e mensal no outro a
 * conta não fecha, e uma decomposição que não multiplica de volta é um erro
 * exibido com cara de rigor.
 *
 * Por isso a verificação: se o produto divergir do ROE do motor, a tela não
 * mostra a decomposição — mostra que ela não reconcilia.
 */
export const decomposicaoDuPont = (leitura) => {
  const balanco = leitura?.balanco;
  const ativo = numeroOuNulo(balanco?.ativo?.total);
  const patrimonio = numeroOuNulo(balanco?.passivo?.patrimonioLiquido?.total);
  if (!(ativo > 0) || !(patrimonio > 0)) return null;

  const lucroMedio = mediaDosConhecidos(leitura?.series?.lucroCaixa);
  const receitaMedia = mediaDosConhecidos(leitura?.series?.receitaLiquida);
  if (lucroMedio === null || !(receitaMedia > 0)) return null;

  const margem = (lucroMedio / receitaMedia) * 100;
  const giro = receitaMedia / ativo;
  const multiplicador = ativo / patrimonio;
  const roeCalculado = (margem / 100) * giro * multiplicador * 100;
  const roeDoMotor = leitura?.valores?.roe ?? null;

  return {
    margem,
    giro,
    multiplicador,
    roeCalculado,
    roeDoMotor,
    // Meio ponto percentual de tolerância cobre arredondamento; mais que isso é
    // base diferente, e base diferente invalida a leitura inteira.
    confere: roeDoMotor === null || Math.abs(roeCalculado - roeDoMotor) <= 0.5
  };
};

/**
 * A linha do tempo do ciclo: quanto tempo o dinheiro fica preso, e onde.
 *
 * Exige os três prazos. Desenhar a régua com dois deles faria o ciclo
 * financeiro parecer menor do que é — e o ciclo é a promessa central do bloco.
 */
export const linhaDoTempoDoCiclo = (leitura) => {
  const { dso, dio, dpo } = leitura?.valores || {};
  if (dso === null || dio === null || dpo === null) return null;
  if (dso === undefined || dio === undefined || dpo === undefined) return null;

  const operacional = dio + dso;
  return {
    pmre: dio,
    pmrv: dso,
    pmpc: dpo,
    operacional,
    financeiro: operacional - dpo,
    // A régua é o maior dos dois trilhos, para os dois ficarem comparáveis.
    escala: Math.max(operacional, dpo, 1)
  };
};

/**
 * A frase que reconcilia faturamento e ponto de equilíbrio.
 *
 * Sem ela, a tela exibe "margem líquida excelente" ao lado de "faturamento
 * abaixo do ponto de equilíbrio" — os dois verdadeiros, os dois calculados
 * sobre bases diferentes, e nenhum dos dois explicado.
 */
export const leituraDoPontoDeEquilibrio = (resumo) => {
  const { faturamento, pontoEquilibrio } = resumo || {};
  if (faturamento === null || pontoEquilibrio === null || faturamento === undefined) return null;
  if (!(pontoEquilibrio > 0)) return null;

  const falta = pontoEquilibrio - faturamento;
  if (falta > 0) {
    return `Faturou ${formatarMoedaCurta(faturamento)} num mês que precisava de ${formatarMoedaCurta(
      pontoEquilibrio
    )} para empatar. Faltaram ${formatarMoedaCurta(falta)} de venda — é por isso que o resultado com CMV fica negativo.`;
  }
  return `Faturou ${formatarMoedaCurta(faturamento)}, ${formatarMoedaCurta(
    -falta
  )} acima do ponto de equilíbrio de ${formatarMoedaCurta(pontoEquilibrio)}.`;
};

/**
 * A ressalva da tendência: com três meses não existe tendência, existe
 * variação. Dizer isso é o que impede o usuário de ler duas subidas seguidas
 * como um padrão do negócio.
 */
export const ressalvaDaTendencia = (leitura) => {
  const informados = leitura?.cobertura?.total ?? leitura?.periodos ?? 0;
  if (informados >= 6) return null;
  if (informados <= 1) return 'Um mês informado: ainda não há variação para observar.';
  return `${informados} meses informados: é variação, ainda não é tendência.`;
};

/**
 * Por que um pilar ficou de fora da nota.
 *
 * "Não entrou" sem motivo é o mesmo que não dizer nada. O usuário precisa saber
 * se falta dado dele (balanço) ou se falta histórico (um mês só não tem
 * variação).
 */
export const motivoDoPilarFora = (pilar, temBalanco) => {
  if (!pilar) return null;
  if (pilar.exigeBalanco && !temBalanco) return 'Depende do balanço patrimonial';
  if (pilar.id === 'crescimento') return 'Depende de dois meses lançados em sequência';
  return 'Sem dado suficiente no período';
};
